import React, { Suspense, lazy, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import ConfirmModal from "../Component/Models/ConfirmModal";
import { AUTH_TOKEN_TEXT } from "../../Constants";
import {
  getAuthUserAction,
  logoutAction,
} from "../../Store/Actions/AuthActions";
import { getLocalData } from "../../utils/LocalStorage";
import { showToast } from "../../utils/helper";
import { trackEvent } from "../../utils/analytics";
import {
  appStoreImg,
  playStoreImg,
  spotAddMobile,
} from "../../Constants/images";

// The claim wizard is the heaviest thing this page can reach, and none of
// it is needed to paint the hero. ClaimSpotModal pulls in AddSpotModal,
// which pulls in @react-google-maps/api — the Maps SDK was being parsed
// on every landing-page view even though it is only used once the visitor
// opens the wizard.
//
// It already renders behind `claimOpen`, so lazy() changes nothing
// functionally: the chunk is fetched on the click that opens it.
const ClaimSpotModal = lazy(() => import("../Component/Models/ClaimSpotModal"));

const LOGO_URL =
  "https://www.getspotie.com/wp-content/uploads/2026/06/logo.png";

const APP_STORE_URL =
  "https://apps.apple.com/us/app/spotie-shared-parking/id6751072842";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.spotieparking";

/**
 * Shared landing layout for the city pages (/dc, /nyc).
 * Renders only the top two parts: the sticky navbar and the
 * full-width hero banner. All styling lives in
 * src/Assets/sass/layouts/_landing.scss, scoped under .spotie-landing.
 */
const Landing = ({ location }) => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((s) => s.auth && s.auth.isAuthenticated);
  const [claimOpen, setClaimOpen] = useState(false);
  const [logoutConfirm, setLogoutConfirm] = useState(false);
  // Only trust the hydrated Redux state — a stale localStorage token
  // shouldn't gate the logout icon by itself. Landing.js's mount effect
  // fires getAuthUserAction if a token exists, so a valid session flips
  // isAuthenticated to true within a beat; an expired one leaves it false.
  const showLogout = !!isAuthenticated;

  useEffect(() => {
    const prevTitle = document.title;
    document.title = `Spotie | List Your Parking Spot in ${location}`;
    return () => {
      document.title = prevTitle;
    };
  }, [location]);

  useEffect(() => {
    trackEvent("web_landing_view", { city: location });
  }, [location]);

  // Landing pages aren't behind GuestRoute (marketing content must render
  // immediately for public visitors), so Redux auth state isn't hydrated
  // by the usual route wrapper. Fire-and-forget on mount if we have a
  // token — ClaimSpotModal reads Redux + falls back to the token when it
  // decides whether to skip step 1.
  useEffect(() => {
    if (getLocalData(AUTH_TOKEN_TEXT)) {
      getAuthUserAction(dispatch).catch(() => {});
    }
  }, [dispatch]);

  const openClaim = (ctaLocation) => (e) => {
    if (e && e.preventDefault) e.preventDefault();
    trackEvent("web_landing_claim_click", {
      city: location,
      cta_location: ctaLocation,
    });
    // Everyone goes through ClaimSpotModal — the wizard branches inside
    // based on auth state (authed users skip step 1 and load any draft).
    setClaimOpen(true);
  };

  const handleCallClick = () => {
    trackEvent("web_landing_call_click", { city: location });
  };

  const handleStoreClick = (eventName) => () => {
    trackEvent(eventName, { city: location });
  };

  const askLogout = () => setLogoutConfirm(true);

  const performLogout = () => {
    trackEvent("web_logout", { role: "user" });
    setLogoutConfirm(false);
    logoutAction(dispatch);
  };

  return (
    <div className="spotie-landing">
      <nav>
        <div className="wrap nav-inner">
          <div className="brand">
            <span className="dot">
              <img src={LOGO_URL} alt="Spotie" width="40" height="40" />
            </span>
            <span className="loc">{location}</span>
          </div>
          <div className="nav-actions">
            <a
              href="tel:+12023940024"
              className="nav-call"
              onClick={handleCallClick}
              aria-label="Call us at 202-394-0024"
            >
              <span className="nav-call-tagline">
                Our team is local like you!
              </span>
              <span className="nav-call-line">
                <i className="fas fa-phone" />
                <span className="nav-call-label">Call us:&nbsp;</span>
                <span className="nav-call-number">202-394-0024</span>
              </span>
            </a>
            <a href="#" className="nav-cta" onClick={openClaim("nav")}>
              Add My Spot
            </a>
            {showLogout && (
              <button
                type="button"
                className="nav-logout"
                onClick={askLogout}
                aria-label="Log out"
                title="Log out"
              >
                <i className="fas fa-sign-out-alt" />
              </button>
            )}
          </div>
        </div>
      </nav>

      <section className="claim">
        <div className="claim-inner">
          <div className="claim-copy">
            <span className="eyebrow">
              Turn your Driveway into Extra Income
            </span>
            <h2>
              We want your <span className="accent">Spot</span>! List it FREE
              and Get <span className="claim-amount">$25</span>
            </h2>
            <p className="claim-offer">
              <strong>Your Spot = Your Cash</strong>
            </p>
            <p>
              Next, tell us if you want to receive your cash via{" "}
              <strong>Zelle</strong> or <strong>Cash App</strong>. If you have a
              call with us, we will add an{" "}
              <span className="claim-highlight">additional $10</span>. We need
              your feedback to help us grow.
            </p>
            <button
              type="button"
              className="btn-primary"
              onClick={openClaim("hero")}
            >
              Add My Spot &rarr;
            </button>
            <div className="app-stores">
              <span className="app-stores-note">
                or you can add your spot using our mobile app
              </span>
              <div className="app-stores-badges">
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="app-store-badge"
                  onClick={handleStoreClick("web_landing_app_store_click")}
                  aria-label="Download on the App Store"
                >
                  {/* Intrinsic size reserves the box before the asset
                      lands, so the CTA above doesn't shift. 3743x1221 is
                      the badge artwork's own aspect ratio. */}
                  <img
                    src={appStoreImg}
                    alt="Download on the App Store"
                    width="374"
                    height="122"
                    loading="lazy"
                    decoding="async"
                  />
                </a>
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="app-store-badge"
                  onClick={handleStoreClick("web_landing_play_store_click")}
                  aria-label="Get it on Google Play"
                >
                  <img
                    src={playStoreImg}
                    alt="Get it on Google Play"
                    width="374"
                    height="122"
                    loading="lazy"
                    decoding="async"
                  />
                </a>
              </div>
            </div>
          </div>
          <div className="claim-visual">
            {/* Not lazy-loaded: on desktop this sits beside the headline
                and is a genuine LCP candidate, so it needs to start
                downloading with the document, not after layout.
                width/height match the source and keep the hero from
                reflowing once it arrives. */}
            <img
              src={spotAddMobile}
              alt="Spotie add-spot screen on a phone"
              width="303"
              height="628"
              fetchPriority="high"
              decoding="async"
            />
          </div>
        </div>
      </section>

      <section className="faq">
        <div className="wrap faq-inner">
          <span className="eyebrow">FAQ</span>
          <h3>
            Common Questions from
            <br />
            {location} Hosts
          </h3>

          <div className="faq-list">
            <details>
              <summary>How does the $25 welcome bonus work?</summary>
              <p>
                Add a free listing of your spot through this page and we'll send
                you $25 via Zelle or Cash App — just tell us which you prefer.
                Have a quick call with our team and we'll add an extra $10. It's
                a one-time bonus for new hosts — no fine print.
              </p>
            </details>

            <details>
              <summary>When and how do I get paid?</summary>
              <p>
                Payouts land in your bank account via Stripe within 2–3 business
                days after each completed booking. You connect your bank once
                from your dashboard and we handle the rest automatically.
              </p>
            </details>

            <details>
              <summary>Who books my spot?</summary>
              <p>
                Local drivers looking for short-term parking — commuters, event
                visitors, neighborhood guests. Every guest verifies their email
                and vehicle before they can book, and you can require approval
                on every request if you want full control.
              </p>
            </details>

            <details>
              <summary>Is parking really in demand in {location}?</summary>
              <p>
                Short answer: yes. Residential streets fill up fast and paid
                lots run $30–$60 a day. A private driveway or pad is often the
                cheapest option a driver can find — which is exactly why your
                spot books.
              </p>
            </details>

            <details>
              <summary>What if I need my spot back?</summary>
              <p>
                You set the availability — block any day, week, or recurring
                window from your dashboard. Existing bookings are honored, but
                no new ones can land on a blocked window.
              </p>
            </details>

            <details>
              <summary>What does Spotie take?</summary>
              <p>
                A small service fee on each booking covers payment processing,
                guest verification, and customer support. The fee is shown
                upfront when you set your hourly or flat rate — what you see is
                what you keep.
              </p>
            </details>

            <details>
              <summary>Is my property protected?</summary>
              <p>
                Every booking goes through identity-verified guests, and
                payments are escrowed by Stripe so you're never chasing money.
                For physical damage, we recommend checking that your homeowner's
                or renter's insurance covers the spot the same way it covers
                your driveway today.
              </p>
            </details>

            <details>
              <summary>How long does it take to list a spot?</summary>
              <p>
                Most hosts finish in under 5 minutes. You'll need a photo of the
                spot, the address, and your availability — then you're live.
              </p>
            </details>
          </div>

          <div className="faq-cta">
            <button
              type="button"
              className="btn-primary"
              onClick={openClaim("faq")}
            >
              Add My Spot &rarr;
            </button>
          </div>
        </div>
      </section>

      {claimOpen && (
        <Suspense fallback={null}>
          <ClaimSpotModal
            show={claimOpen}
            city={location}
            onClose={() => setClaimOpen(false)}
            onSuccess={() => {
              setClaimOpen(false);
              showToast(
                "success",
                "Your spot is in! Check your email for next steps.",
              );
            }}
          />
        </Suspense>
      )}

      <ConfirmModal
        show={logoutConfirm}
        title="Log out"
        message="Are you sure you want to log out?"
        confirmLabel="Yes, log out"
        cancelLabel="No"
        onClose={() => setLogoutConfirm(false)}
        onConfirm={performLogout}
      />
    </div>
  );
};

export default Landing;
